import { z } from "zod";
import { BASE, HUE, ISO, MONEY } from "./common.ts";

export const COMPLIANCE = z.enum(["complete", "expiring", "missing"]);
export type Compliance = z.infer<typeof COMPLIANCE>;

export const FREELANCER_DOCUMENT = z.object({ kind: z.string(), expiresAt: ISO.optional() });
export type FreelancerDocument = z.infer<typeof FREELANCER_DOCUMENT>;

/** One key per address, so the same link written two ways counts once. */
const linkKey = (url: string) => {
  try {
    return new URL(url).href;
  } catch {
    return url;
  }
};

/** A piece of past work a freelancer shows the team: a title and a secure link, https only (X11-625b). */
export const PORTFOLIO_ITEM = z.strictObject({
  title: z.string().trim().min(1).max(120),
  url: z.url({ protocol: /^https$/, error: "Use a secure link for the portfolio item." }).max(500),
});
export type PortfolioItem = z.infer<typeof PORTFOLIO_ITEM>;

/** Up to 20 items; a link may appear only once. */
export const PORTFOLIO = z.array(PORTFOLIO_ITEM).max(20).superRefine((items, ctx) => {
  const seen = new Set<string>();
  items.forEach((item, i) => {
    const key = linkKey(item.url);
    if (seen.has(key)) ctx.addIssue({ code: "custom", path: [i, "url"], message: "This link is already in the portfolio." });
    seen.add(key);
  });
});

/** One answer set from the classification questionnaire (IR35 or AB5), with the result it gave and the risk of that result. */
export const CLASSIFICATION = z.object({
  id: z.string(),
  at: ISO,
  regime: z.enum(["ir35", "ab5", "us1099"]),
  answers: z.record(z.string(), z.boolean()),
  result: z.enum(["employee", "contractor", "review"]),
  risk: z.enum(["low", "medium", "high"]),
  reasons: z.array(z.string()),
  by: z.string(),
});
export type Classification = z.infer<typeof CLASSIFICATION>;

/** A contractor on the bench. `availability` holds 12 numbers: hours free per week, for the next 12 weeks. */
export const FREELANCER = BASE.extend({
  name: z.string(),
  title: z.string(),
  skills: z.array(z.string()),
  rate: MONEY,
  /** The hourly, daily and fixed rates (X11-625a). `rate` stays the default: the hourly rate when there is one. */
  rates: z.strictObject({ hourly: MONEY.optional(), daily: MONEY.optional(), fixed: MONEY.optional() }).optional(),
  /** Links to past work the team can show, each a title and a secure link (X11-625b). */
  portfolio: PORTFOLIO.default([]),
  hue: HUE,
  timeZone: z.string(),
  availability: z.array(z.number()).length(12),
  rating: z.number().min(1).max(5),
  compliance: COMPLIANCE,
  documents: z.array(FREELANCER_DOCUMENT),
  marginPct: z.number(),
  country: z.string(),
  /** Person id of the workspace member this freelancer signs in as, when there is one. */
  personId: z.string().optional(),
  /** Identity check from onboarding: the method and the provider's reference. The document itself is never stored (X2-13). */
  idCheck: z.object({ method: z.string(), reference: z.string(), checkedAt: ISO }).optional(),
  /** Safe preview of the payout account (last four characters). The account itself is sealed in the vault. */
  payoutHint: z.string().optional(),
  /** How the freelancer chose to be paid on their onboarding link (X11-626a): a bank account or Wise. */
  payoutMethod: z.enum(["bank", "wise"]).optional(),
  /** Which payout rail pays this freelancer. */
  payoutRail: z.enum(["wise", "stripe", "paypal", "payoneer", "deel"]).optional(),
  /** Dated IR35 and AB5 determinations, newest last (X2-13). */
  classifications: z.array(CLASSIFICATION).optional(),
});
export type Freelancer = z.infer<typeof FREELANCER>;

export const WORK_ORDER_STATE = z.enum(["draft", "active", "done", "declined"]);
export type WorkOrderState = z.infer<typeof WORK_ORDER_STATE>;

/** A freelancer's submission against a work order: hours for a timesheet, or an amount for a milestone (X2-13). */
export const WORK_SUBMISSION = z.object({
  id: z.string(),
  kind: z.enum(["timesheet", "milestone"]),
  at: ISO,
  note: z.string(),
  hours: z.number().optional(),
  amount: MONEY.optional(),
  state: z.enum(["submitted", "approved", "rejected"]),
  reason: z.string().optional(),
  payoutId: z.string().optional(),
});
export type WorkSubmission = z.infer<typeof WORK_SUBMISSION>;

/** A freelancer's scoped piece of work on a project, capped in hours. */
export const WORK_ORDER = BASE.extend({
  freelancerId: z.string(),
  projectId: z.string(),
  title: z.string(),
  capHours: z.number(),
  usedHours: z.number(),
  rate: MONEY,
  state: WORK_ORDER_STATE,
  /** Why the freelancer turned the work order down. Set when `state` is `declined`. */
  declineReason: z.string().optional(),
  /** Timesheet and milestone submissions, newest last. Pending hours count against the cap (X2-13). */
  submissions: z.array(WORK_SUBMISSION).optional(),
  /** The requisition this work order fills. */
  requisitionId: z.string().optional(),
  /** The agreement this order is signed under. Nobody can submit hours or a milestone until it is signed (X11-627a). */
  agreementId: z.string().optional(),
  /** The team's rating of the finished job, 1 to 5. Staff only; it moves the freelancer's `rating` (X9-196a). */
  rating: z.number().int().min(1).max(5).optional(),
  /** Internal note on the rating. Staff only. */
  ratingNote: z.string().max(500).optional(),
  /** When the rating was last given or changed. */
  ratedAt: ISO.optional(),
});
export type WorkOrder = z.infer<typeof WORK_ORDER>;

export const PAYOUT_STATE = z.enum(["pending", "approved", "paid", "failed", "rejected"]);
export type PayoutState = z.infer<typeof PAYOUT_STATE>;

/** A payment to a freelancer for one period (`2026-09` style). `invoiceRef` is the self-billing invoice number. */
export const PAYOUT = BASE.extend({
  freelancerId: z.string(),
  amount: MONEY,
  period: z.string(),
  state: PAYOUT_STATE,
  invoiceRef: z.string(),
  /** The work order whose approved submission produced this payout. */
  workOrderId: z.string().optional(),
  /** The payout rail that sent it, and the provider's own reference once it is sent (X2-13). */
  rail: z.enum(["wise", "stripe", "paypal", "payoneer", "deel"]).optional(),
  providerRef: z.string().optional(),
  /** The supplier bill this payout raised when it was approved (one per payout, never a second). */
  billId: z.string().optional(),
  /** The approval that decides this payout, opened by a "freelancer-invoice" rule (X13-1410a). One per payout while it is pending. */
  approvalId: z.string().optional(),
});
export type Payout = z.infer<typeof PAYOUT>;

export const JOB = BASE.extend({
  title: z.string(),
  department: z.string(),
  kind: z.enum(["employee", "freelancer"]),
  state: z.enum(["open", "draft", "closed"]),
  applicants: z.number(),
  openedAt: ISO,
  ownerId: z.string(),
});
export type Job = z.infer<typeof JOB>;

/** The built-in meaning of a hiring stage (X9-169a): the rules that book interviews and close candidates read this, whatever
 *  the agency calls the stage. */
export const CANDIDATE_STAGE_KIND = z.enum(["applied", "screen", "interview", "offer", "hired", "rejected"]);
export type CandidateStageKind = z.infer<typeof CANDIDATE_STAGE_KIND>;
/** A candidate's stage: the id of an entry of the workspace setting `candidateStages` (X9-169a). */
export const CANDIDATE_STAGE = z.string().trim().min(1).max(40);
export type CandidateStage = z.infer<typeof CANDIDATE_STAGE>;

/** A scheduled conversation with a candidate, held by one staff interviewer (X7-05c). */
export const INTERVIEW = z.object({ id: z.string(), at: ISO, interviewerId: z.string(), state: z.enum(["scheduled", "done", "cancelled"]) });
export type Interview = z.infer<typeof INTERVIEW>;

export const RECOMMENDATION = z.enum(["strong_yes", "yes", "no", "strong_no"]);
export type Recommendation = z.infer<typeof RECOMMENDATION>;

/** One interviewer's ratings of a candidate. `average` is the mean of the ratings, rounded to one decimal. */
export const SCORECARD = z.object({
  id: z.string(), interviewerId: z.string(), interviewId: z.string().optional(), at: ISO,
  ratings: z.array(z.object({ criterion: z.string().min(1).max(80), rating: z.number().int().min(1).max(5) })).min(1).max(12),
  recommendation: RECOMMENDATION, note: z.string().max(2000).optional(), average: z.number(),
});
export type Scorecard = z.infer<typeof SCORECARD>;

export const CANDIDATE = BASE.extend({
  jobId: z.string(),
  name: z.string(),
  stage: CANDIDATE_STAGE,
  score: z.number().optional(),
  source: z.string(),
  appliedAt: ISO,
  nextInterviewAt: ISO.optional(),
  interviews: z.array(INTERVIEW).optional(),
  scorecards: z.array(SCORECARD).optional(),
  /** The talent pool entry made when the candidate was hired onto a freelancer job (X9-171a). */
  freelancerId: z.string().optional(),
});
export type Candidate = z.infer<typeof CANDIDATE>;

export const AGENDA_ITEM = z.object({ id: z.string(), text: z.string(), by: z.string(), done: z.boolean() });
export type AgendaItem = z.infer<typeof AGENDA_ITEM>;

export const FOLLOW_UP = z.object({ text: z.string(), done: z.boolean() });
export type FollowUp = z.infer<typeof FOLLOW_UP>;

/** A recurring manager and report conversation. `personId` is the report, `managerId` the manager. */
export const ONE_ON_ONE = BASE.extend({
  personId: z.string(),
  managerId: z.string(),
  nextAt: ISO,
  agenda: z.array(AGENDA_ITEM),
  notes: z.string(),
  followUps: z.array(FOLLOW_UP),
});
export type OneOnOne = z.infer<typeof ONE_ON_ONE>;

export const SKILL_SCORE = z.object({ personId: z.string(), skill: z.string(), levels: z.array(z.number().int().min(0).max(4)) });
export type SkillScore = z.infer<typeof SKILL_SCORE>;

export const REVIEW_CYCLE_STATE = z.enum(["planned", "active", "closed"]);
export type ReviewCycleState = z.infer<typeof REVIEW_CYCLE_STATE>;

export const REVIEW_CYCLE = BASE.extend({
  name: z.string(),
  state: REVIEW_CYCLE_STATE,
  dueAt: ISO,
  completed: z.number(),
  total: z.number(),
  skills: z.array(SKILL_SCORE),
});
export type ReviewCycle = z.infer<typeof REVIEW_CYCLE>;

/** A written policy staff must read and acknowledge. */
export const POLICY = BASE.extend({
  title: z.string(),
  version: z.string(),
  required: z.boolean(),
  acknowledged: z.number(),
  total: z.number(),
  ownerId: z.string(),
});
export type Policy = z.infer<typeof POLICY>;

export const REQUISITION_STAGE = z.enum(["open", "shortlist", "offered", "filled"]);
export type RequisitionStage = z.infer<typeof REQUISITION_STAGE>;

/** A freelancer role a project needs filled. `candidates` holds freelancer ids, `neededBy` is the start day. */
export const REQUISITION = BASE.extend({
  title: z.string(),
  projectId: z.string(),
  skills: z.array(z.string()),
  hours: z.number(),
  rate: MONEY,
  stage: REQUISITION_STAGE,
  candidates: z.array(z.string()),
  neededBy: ISO,
  /** Set while an offer is out or after it is accepted. */
  freelancerId: z.string().nullable().optional(),
  workOrderId: z.string().nullable().optional(),
});
export type Requisition = z.infer<typeof REQUISITION>;
